import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { ValuationData, ValidationErrors } from '../../../types/valuation';
import { Tooltip } from '../../ui/Tooltip';
import { Button } from '../../ui/Button';
import { useAddressAutocomplete } from '../../../hooks/useAddressAutocomplete';
import { Link } from 'react-router-dom';
import SurplusLandDisclaimerModal from '../SurplusLandDisclaimerModal';
import { supabase } from '../../../lib/supabase';

interface BasicInfoProps {
  data: ValuationData;
  onUpdate: (data: Partial<ValuationData>) => void;
  errors?: ValidationErrors;
}

const fuelBrands = [
  'Chevron',
  'Shell',
  'Arco',
  'Sinclair',
  'BP',
  'ConocoPhillips 76',
  'Exxon',
  'Mobil',
  'Texaco',
  'Valero',
  '7-Eleven',
  'Circle K',
  'Other'
];

// Store brands will be loaded from Supabase brand_tiers table
const DEFAULT_STORE_BRANDS = [
  'AM/PM',
  'Dino Mart',
  'Extra Mile',
  'Food Mart',
  '7-Eleven',
  'Circle K',
  'Independent'
];

// Brands that must always appear in the dropdown even if missing from
// brand_tiers (frontend-side guarantees).
const REQUIRED_STORE_BRANDS = ['AM/PM', 'Food Mart', 'Independent'];

// Map legacy/inconsistent DB labels to the canonical display name.
// Lookup is case-insensitive; the value is what we render and store.
const STORE_BRAND_NORMALIZATION: Record<string, string> = {
  'ampm': 'AM/PM',
  'am/pm': 'AM/PM',
  'amPM': 'AM/PM',
  'food mart': 'Food Mart',
  'foodmart': 'Food Mart',
  'independent': 'Independent',
  'n/a': 'Independent',
  'na': 'Independent',
  '7-11': '7-Eleven',
  '7eleven': '7-Eleven',
  'seven eleven': '7-Eleven',
};

const normalizeStoreBrand = (name: string | null | undefined): string => {
  if (!name) return '';
  const trimmed = name.trim();
  return STORE_BRAND_NORMALIZATION[trimmed.toLowerCase()] || trimmed;
};

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Modal component for alerts
const AlertModal = ({ isOpen, onClose, title, message, type }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type: 'warning' | 'error';
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center gap-3 mb-4">
          {type === 'warning' ? (
            <AlertTriangle className="w-6 h-6 text-amber-500" />
          ) : (
            <AlertCircle className="w-6 h-6 text-red-500" />
          )}
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        <p className="text-gray-700 mb-6">{message}</p>
        <div className="flex justify-end">
          <Button onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function BasicInfo({ data, onUpdate, errors = {} }: BasicInfoProps) {
  const [addressInput, setAddressInput] = useState(data.location || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [addressIsValid, setAddressIsValid] = useState(!!data.location);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // State for modals
  const [showRedFlagModal, setShowRedFlagModal] = useState(false);
  const [showUSTModal, setShowUSTModal] = useState(false);
  const [showSurplusLandModal, setShowSurplusLandModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const { results: addressResults, isLoading } = useAddressAutocomplete(addressInput);
  console.log('addressResults in BasicInfo:', addressResults);

  // State for store brands loaded from Supabase
  const [storeBrands, setStoreBrands] = useState<string[]>(DEFAULT_STORE_BRANDS);

  // Automatically set ownership to 'owned' since it's the only supported option
  useEffect(() => {
    if (!data.ownership || data.ownership !== 'owned') {
      onUpdate({ ownership: 'owned' });
    }
  }, [data.ownership, onUpdate]);

  // Fetch store brands from Supabase brand_tiers table (convenience tier only)
  useEffect(() => {
    const fetchStoreBrands = async () => {
      try {
        const { data: brands, error } = await supabase
          .from('brand_tiers')
          .select('brand_name, tier')
          .eq('active', true)
          .order('brand_name');

        if (error) {
          console.error('Error fetching store brands:', error);
          return;
        }

        if (brands && brands.length > 0) {
          // Filter for convenience store brands only (exclude fuel brands)
          const cStoreBrands = brands.filter((b: any) =>
            b.tier && b.tier.toLowerCase().includes('convenience')
          );

          // Normalize legacy labels (e.g. 'ampm' → 'AM/PM') and dedupe
          const normalized = cStoreBrands
            .map((b: any) => normalizeStoreBrand(b.brand_name))
            .filter((name: string) => name.length > 0);
          const merged = Array.from(new Set([...normalized, ...REQUIRED_STORE_BRANDS]));

          setStoreBrands(merged.sort());
        }
      } catch (err) {
        console.error('Failed to load store brands:', err);
      }
    };

    fetchStoreBrands();
  }, []);

  // Handle outside click to close dropdown
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddressInput(value);
    setShowDropdown(true);
    setAddressIsValid(false); // Reset validation when user edits
    onUpdate({ location: value });
  };

  // Highlight matching substring in suggestion
  const renderHighlightedText = (text: string, query: string) => {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-yellow-200 font-semibold p-0 m-0 rounded-sm">{text.slice(idx, idx + query.length)}</mark>
        {text.slice(idx + query.length)}
      </>
    );
  };

  // Function to check for red flags in property data
  const checkForRedFlags = (rec: any): boolean => {
    // Check for red flag in property data (case-sensitive match with database field)
    return (
      rec.Red_Flag === 'true' || 
      rec.Red_Flag === true ||
      (rec.BROWNFIELD_STATUS && rec.BROWNFIELD_STATUS.toLowerCase().includes('contaminated'))
    );
  };

  // Function to check UST compliance
  const checkUSTCompliance = (rec: any): boolean => {
    // Sample check logic - check UST compliance based on age and conditions
    const ustAge = rec.UST_Age;
    const ustCondition = rec.UST_Condition;

    // Poor condition or age > 25 years could indicate non-compliance
    return (
      (ustCondition && ustCondition.toLowerCase().includes('poor')) ||
      (ustAge && parseInt(ustAge, 10) > 25)
    );
  };

  // Function to check for surplus excess land
  const checkSurplusExcessLand = (rec: any): boolean => {
    // Check if property has surplus excess land flag
    return (
      rec.Surplus_Excess_Land === 'true' ||
      rec.Surplus_Excess_Land === true
    );
  };

  // State for the red flag reason type
  const [redFlagReason, setRedFlagReason] = useState<'ust' | 'other'>('other');

  // Handle property selection with gating logic
  const handleSelectAddress = (rec: any) => {
    const fullAddress = `${rec.Facility_Address}, ${rec.City}, ${rec.STATE} ${rec.ZIP_Code}`;
    setAddressInput(fullAddress);
    setShowDropdown(false);
    setSelectedProperty(rec);

    // Check for red flags - show modal if found (highest priority - stops valuation)
    if (checkForRedFlags(rec)) {
      // Check if the red flag is due to UST compliance issues
      if (rec.UST_Compliance === false || rec.UST_Compliance === 'false') {
        setRedFlagReason('ust');
      } else {
        setRedFlagReason('other');
      }
      setShowRedFlagModal(true);
      return;
    }

    // Check UST compliance - show warning modal but allow proceeding
    if (checkUSTCompliance(rec)) {
      setShowUSTModal(true);
      return;
    }

    // Check for surplus excess land - show disclaimer modal but allow proceeding
    if (checkSurplusExcessLand(rec)) {
      setShowSurplusLandModal(true);
      return;
    }

    // If no issues, proceed with prepopulation
    prepopulatePropertyData(rec, fullAddress);
  };
  
  // Store database values for suggestions instead of pre-populating
  const prepopulatePropertyData = (rec: any, fullAddress: string) => {
    setAddressIsValid(true); // Address is valid when selected from dropdown

    console.log('=== BasicInfo: prepopulatePropertyData called ===');
    console.log('rec.ObjectID:', rec.ObjectID);
    console.log('rec.OBJECTID:', rec.OBJECTID); // Try both cases
    console.log('fullAddress:', fullAddress);
    console.log('Full rec object:', rec);

    const objectId = rec.ObjectID || rec.OBJECTID;
    console.log('Using objectId:', objectId);

    // Only set location and station ID - don't pre-populate the size/year fields
    // Store the database values for later use as suggestions in PropertyDetails
    const updatePayload = {
      location: fullAddress,
      stationObjectId: objectId,
      // Store database values for suggestions but don't pre-fill the fields
      databaseSuggestions: {
        propertySize: rec.Cstore_Size_Measured != null ? Number(rec.Cstore_Size_Measured) : undefined,
        canopySize: rec.Fueling_Canopy_Size_Measured != null ? Number(rec.Fueling_Canopy_Size_Measured) : undefined,
        lotSize: rec.Lot_Size != null ? Number(rec.Lot_Size) : undefined,
        yearBuilt: rec.Year_Built_Public_Record != null ? Number(rec.Year_Built_Public_Record) : undefined,
        carWashSize: rec.Car_Wash_Size_Measured != null ? Number(rec.Car_Wash_Size_Measured) : undefined,
        carWashLength: rec.Car_Wash_Length != null ? Number(rec.Car_Wash_Length) : undefined
      },
      // Only set car wash flag if the station actually has one. The boolean
      // Car_Wash column is the source of truth; size/length are 0 (not null)
      // for stations without a car wash, so a `!= null` check would falsely
      // mark every station with a 0 measurement as having an automatic wash.
      hasCarWash: (() => {
        if (rec.Car_Wash === true) return true;
        const size = rec.Car_Wash_Size_Measured != null ? Number(rec.Car_Wash_Size_Measured) : 0;
        const length = rec.Car_Wash_Length != null ? Number(rec.Car_Wash_Length) : 0;
        return size > 0 || length > 0;
      })(),
      carWashType: (() => {
        if (rec.Car_Wash === true) return 'automatic';
        const size = rec.Car_Wash_Size_Measured != null ? Number(rec.Car_Wash_Size_Measured) : 0;
        const length = rec.Car_Wash_Length != null ? Number(rec.Car_Wash_Length) : 0;
        return size > 0 || length > 0 ? 'automatic' : undefined;
      })()
    };

    console.log('Calling onUpdate with payload:', updatePayload);
    onUpdate(updatePayload);
  };
  
  // Handle modal closures
  const handleRedFlagModalClose = () => {
    setShowRedFlagModal(false);
    // Reset selection
    setSelectedProperty(null);
    setAddressInput('');
  };
  
  const handleUSTModalClose = () => {
    setShowUSTModal(false);
    // Allow proceeding with the property despite UST issues
    if (selectedProperty) {
      const fullAddress = `${selectedProperty.Facility_Address}, ${selectedProperty.City}, ${selectedProperty.STATE} ${selectedProperty.ZIP_Code}`;
      prepopulatePropertyData(selectedProperty, fullAddress);
    }
  };

  const handleSurplusLandModalClose = () => {
    setShowSurplusLandModal(false);
    // Reset selection - user chose to cancel
    setSelectedProperty(null);
    setAddressInput('');
  };

  const handleSurplusLandModalProceed = () => {
    setShowSurplusLandModal(false);
    // User acknowledged the disclaimer, proceed with prepopulation
    if (selectedProperty) {
      const fullAddress = `${selectedProperty.Facility_Address}, ${selectedProperty.City}, ${selectedProperty.STATE} ${selectedProperty.ZIP_Code}`;
      prepopulatePropertyData(selectedProperty, fullAddress);
    }
  };

  // Function to validate address before proceeding
  React.useEffect(() => {
    // Update validation errors for address
    if (!data.location) {
      onUpdate({ validationErrors: { ...data.validationErrors, location: 'Address is required' } });
    } else if (!addressIsValid) {
      onUpdate({ validationErrors: { ...data.validationErrors, location: 'Please select an address from the dropdown' } });
    } else {
      // Remove the location error if it's valid
      const newErrors = { ...data.validationErrors };
      if (newErrors.location) delete newErrors.location;
      onUpdate({ validationErrors: newErrors });
    }
  }, [data.location, addressIsValid]);

  return (
    <div className="space-y-6">
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <p className="text-sm text-slate-700">
          Start by telling us about your gas station's location and brand. This helps us pull comparable properties and tailor the valuation to your specific market area.
        </p>
      </div>
      <div>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-700">
            Currently serving Riverside, San Bernardino, and Orange Counties. Additional counties coming soon.
          </p>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Location
          </label>
          <Tooltip content="Enter the full address of the property">
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <div className="relative">
          <input
            type="text"
            value={addressInput}
            onChange={handleAddressChange}
            onFocus={() => setShowDropdown(true)}
            autoComplete="off"
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.location ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter property address"
            required
          />
          <div className="mt-1 flex items-center">
            <div className="text-sm text-blue-600">
              Don't see your station in our database? <Link to="/contact-us" className="underline hover:text-blue-800">Give us a shout and click here</Link>
            </div>
          </div>
          {showDropdown && (
            <div
              ref={dropdownRef}
              className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-auto"
            >
              {addressResults.length === 0 && !isLoading && (
                <div className="px-4 py-2 text-gray-400 text-sm">No addresses found.</div>
              )}
              {addressResults.map((rec) => {
                const full = `${rec.Facility_Address}, ${rec.City}, ${rec.STATE} ${rec.ZIP_Code}`;
                return (
                  <button
                    type="button"
                    key={rec.OBJECTID || rec.id}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50"
                    onClick={() => handleSelectAddress(rec)}
                  >
                    {renderHighlightedText(full, addressInput)}
                  </button>
                );
              })}
              {isLoading && (
                <div className="px-4 py-2 text-gray-400 text-sm">Loading...</div>
              )}
            </div>
          )}
        </div>
        {errors.location && (
          <p className="mt-1 text-sm text-red-500">{errors.location}</p>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Property Ownership
          </label>
          <Tooltip content="Currently only owned properties are supported for valuation">
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-medium">
          Owned
        </div>
        <p className="mt-1 text-sm text-blue-600">
          Leased ownership (landlord position) feature coming Q3 2026
        </p>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Brand Affiliation
          </label>
          <Tooltip content="Select whether the station is branded or unbranded">
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <select
          value={data.brandAffiliation}
          onChange={(e) => onUpdate({ brandAffiliation: e.target.value as 'branded' | 'unbranded' })}
          className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.brandAffiliation ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select brand affiliation</option>
          <option value="branded">Branded</option>
          <option value="unbranded">Unbranded / Independent</option>
        </select>
        {errors.brandAffiliation && (
          <p className="mt-1 text-sm text-red-500">{errors.brandAffiliation}</p>
        )}

        {data.brandAffiliation === 'branded' && (
          <>
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Fuel Brand
                </label>
                <Tooltip content="Select the fuel brand affiliated with this station">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </Tooltip>
              </div>
              <select
                value={data.brandName}
                onChange={(e) => onUpdate({ brandName: e.target.value })}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.brandName ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select fuel brand</option>
                {fuelBrands.map((brand) => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
              {errors.brandName && (
                <p className="mt-1 text-sm text-red-500">{errors.brandName}</p>
              )}
            </div>

            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Store Brand
                </label>
                <Tooltip content="Select the convenience store brand">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </Tooltip>
              </div>
              <select
                value={normalizeStoreBrand(data.storeBrand)}
                onChange={(e) => onUpdate({ storeBrand: normalizeStoreBrand(e.target.value) })}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.storeBrand ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select store brand</option>
                {storeBrands.map((brand) => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
              {errors.storeBrand && (
                <p className="mt-1 text-sm text-red-500">{errors.storeBrand}</p>
              )}
            </div>
          </>
        )}
        {/* Alert Modals */}
        <AlertModal 
          isOpen={showRedFlagModal}
          onClose={handleRedFlagModalClose}
          title="Property Red Flag Alert"
          message={redFlagReason === 'ust' ?
            "This property has been flagged in our system due to known environmental concerns with underground storage tanks. Please contact a representative for more information and alternative options." :
            "This property has been flagged in our system due to extraordinary circumstances (such as travel center configuration, non-operational fueling, or other special conditions). We are unable to proceed with a credible price estimate. Please contact our valuation team for a customized assessment."}
          type="error"
        />
        
        <AlertModal
          isOpen={showUSTModal}
          onClose={handleUSTModalClose}
          title="UST Compliance Warning"
          message="This property may have underground storage tank (UST) compliance issues that could impact valuation. You can proceed with the evaluation, but we recommend consulting with an environmental specialist."
          type="warning"
        />

        <SurplusLandDisclaimerModal
          isOpen={showSurplusLandModal}
          onClose={handleSurplusLandModalClose}
          onProceed={handleSurplusLandModalProceed}
        />
      </div>
    </div>
  );
}